import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';

import { useEffect, useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { ChevronDown, ChevronUp, CheckCircle, XCircle, Mail } from 'lucide-react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSponsorshipForAdvisorByAdvisorIdRequest,
  postCancellationForSponsorshipsBySponsorshipIdRequest,
  putInvitationResponseForSponsorshipsBySponsorshipIdRequest,
  selectSessionAdvisor,
  selectSponsorshipAccessStatus,
  selectSponsorshipId,
  selectSponsorshipState,
} from '@client/model';
import { AdvisorSponsorshipStatus } from '@shared';
import { Skeleton } from '@client/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@client/components/ui/dialog';

interface ActiveSponsorshipCardProps {
  submitted: number;
  required: number;
  advisorId: string;
  sponsorshipId: string;
}
interface InvitedSponsorshipCardProps {
  required: number;
  advisorId: string;
  sponsorshipId: string;
}

const renderClientNumberString = (numberOfClients: number) => {
  return `${numberOfClients} client${numberOfClients > 1 ? 's' : ''}`;
};

function ActiveSponsorshipCard({
  submitted,
  required,
  advisorId,
  sponsorshipId,
}: ActiveSponsorshipCardProps) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const hasMetCriteria = submitted >= required;
  const progress = Math.min((submitted / required) * 100, 100);

  return (
    <>
      <Card className="border-primary-200 mt-14 max-w-[690px] border">
        <CardContent className="flex flex-col gap-5 p-6">
          <h2 className="text-primary-dark text-lg font-medium">Your Monthly Sponsorship Status</h2>
          <p className="text-primary-dark text-base opacity-70">
            Criteria: Have at least{' '}
            <span className="font-semibold">{renderClientNumberString(required)}</span> submit an
            intake form this month.
          </p>

          <div>
            <p className="text-primary-dark mb-2 text-base opacity-70">Progress:</p>
            <Progress
              value={progress}
              className="h-2"
            />
            <p className="text-primary-dark mt-3 text-base opacity-70">
              {submitted} of {required} clients submitted an intake form.
            </p>

            <div
              className={cn('mt-3 flex items-center gap-1 text-sm font-normal', {
                'text-green-600': hasMetCriteria,
                'text-red-700': !hasMetCriteria,
              })}
            >
              {hasMetCriteria ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  You've met this month's sponsorship criteria!
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4" />
                  You have not yet met the criteria.
                </>
              )}
            </div>
          </div>

          <hr className="border-gray-200" />

          <button
            className="text-primary-dark flex w-full items-center justify-between text-xl font-medium"
            onClick={() => setExpanded(prev => !prev)}
          >
            Manage Sponsorship
            {expanded ? <ChevronUp className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
          </button>

          {expanded && (
            <div className="pt-2">
              <Button
                variant="destructive"
                className="w-max-40 h-12 bg-red-100 text-sm font-medium text-red-700 hover:bg-red-200"
                onClick={() => setShowCancelConfirm(true)}
              >
                Cancel Sponsorship
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Sponsorship</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">
            Are you sure you want to cancel your sponsorship? This action cannot be undone.
          </p>
          <DialogFooter className="mt-4 flex justify-end gap-3">
            <Button
              variant="destructive"
              onClick={() => {
                dispatch(
                  postCancellationForSponsorshipsBySponsorshipIdRequest({
                    sponsorshipId,
                    advisorId,
                  }),
                );
              }}
            >
              Yes, Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InvitedSponsorshipCard({
  advisorId,
  sponsorshipId,
  required,
}: InvitedSponsorshipCardProps) {
  const dispatch = useDispatch();

  return (
    <Card className="mt-14 max-w-[690px] rounded-lg border p-8">
      <CardContent className="flex flex-col gap-5 p-0">
        <h2 className="text-primary-dark text-2xl font-medium leading-8">
          You're invited to join the Sponsorship Program
        </h2>
        <p className="text-secondary text-primary-dark text-base leading-6 text-opacity-70">
          To qualify for monthly sponsorship, you must have at least{' '}
          {renderClientNumberString(required)} submit an intake form this month. By accepting, you
          agree to the{' '}
          <a
            href="https://www.waterlily.com/sponsored-terms"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-dark underline"
          >
            terms of the program.
          </a>
        </p>
        <div className="flex gap-3">
          <Button
            className="h-12 w-40 rounded-xl"
            onClick={() => {
              dispatch(
                putInvitationResponseForSponsorshipsBySponsorshipIdRequest({
                  sponsorshipId,
                  advisorId,
                  status: 'accept',
                }),
              );
            }}
          >
            Accept Invitation
          </Button>
          <Button
            className="h-12 w-32 rounded-xl"
            variant="outline"
            onClick={() => {
              dispatch(
                putInvitationResponseForSponsorshipsBySponsorshipIdRequest({
                  sponsorshipId,
                  advisorId,
                  status: 'decline',
                }),
              );
            }}
          >
            Decline
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function PlaceholderSponsorshipCard() {
  return (
    <Card className="border-primary-200 mt-14 max-w-[690px] border">
      <CardContent className="flex flex-col gap-5 p-6">
        <Skeleton className="h-6 w-2/3 rounded" />
        <Skeleton className="h-5 w-3/4 rounded" />

        <div className="space-y-3">
          <Skeleton className="h-5 w-20 rounded" />
          <Skeleton className="h-2 w-full rounded" />
          <Skeleton className="h-5 w-1/2 rounded" />
          <Skeleton className="h-5 w-2/3 rounded" />
        </div>

        <hr className="border-gray-200" />

        <div className="flex w-full items-center justify-between">
          <Skeleton className="h-10 w-40 rounded" />
          <Skeleton className="h-6 w-6 rounded-full" />
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorSponsorshipCard() {
  return (
    <Card className="mt-14 max-w-[690px] border border-red-200">
      <CardContent className="flex flex-col gap-5 p-6">
        <div className="flex items-center gap-2">
          <XCircle className="h-6 w-6 text-red-700" />
          <h2 className="text-lg font-medium text-red-600">Sponsorship Error</h2>
        </div>
        <p className="text-base text-gray-700">
          We're experiencing issues loading your sponsorship information. This could be due to a
          temporary system issue or a problem with your sponsorship status.
        </p>
        <p className="text-base text-gray-700">
          Please contact our support team to resolve this issue and ensure your sponsorship is
          properly configured.
        </p>
        <div className="mt-4">
          <Button
            className="flex items-center gap-2 rounded-xl"
            variant="secondary"
            onClick={() => {
              window.open('mailto:hello@waterlily.com', '_blank');
            }}
          >
            <Mail className="h-4 w-4" />
            Email Support
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SponsorshipCard({
  advisorSponsorshipStatus,
  advisorId,
}: {
  advisorSponsorshipStatus: AdvisorSponsorshipStatus | null;
  advisorId: string;
}) {
  const sponsorshipId = useSelector(selectSponsorshipId);
  const { activityAmount, activityThreshold, isLoading, isError } =
    useSelector(selectSponsorshipState);

  if (isLoading || advisorSponsorshipStatus === null) {
    return <PlaceholderSponsorshipCard />;
  }

  if (isError) {
    return <ErrorSponsorshipCard />;
  }

  if (advisorSponsorshipStatus === AdvisorSponsorshipStatus.invited) {
    return (
      <InvitedSponsorshipCard
        advisorId={advisorId}
        sponsorshipId={sponsorshipId}
        required={activityThreshold}
      />
    );
  }

  return (
    <ActiveSponsorshipCard
      submitted={activityAmount}
      required={activityThreshold}
      sponsorshipId={sponsorshipId}
      advisorId={advisorId}
    />
  );
}

function useSponsorshipAccess() {
  const navigate = useNavigate();
  const { isPartOfSponsorship, advisorSponsorshipStatus } = useSelector(
    selectSponsorshipAccessStatus,
  );

  useEffect(() => {
    if (!isPartOfSponsorship || advisorSponsorshipStatus === AdvisorSponsorshipStatus.inactive) {
      navigate('/');
    }
  }, [isPartOfSponsorship, navigate]);

  return {
    navigate,
    advisorSponsorshipStatus,
  };
}

export function AdvisorAccountSponsorship() {
  const { advisorSponsorshipStatus } = useSponsorshipAccess();
  const { advisorId } = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchSponsorshipForAdvisorByAdvisorIdRequest(advisorId));
  }, [dispatch]);

  return (
    <div className="flex-1">
      <main className="bg-primary-50 h-screen grow px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="my-7 flex flex-col justify-between">
          <h1 className="text-primary-dark text-4xl font-medium">Sponsorship Program</h1>
          <SponsorshipCard
            advisorSponsorshipStatus={advisorSponsorshipStatus}
            advisorId={advisorId}
          />
        </div>
      </main>
    </div>
  );
}
